/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    useMultiStyleConfig,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {getPDPath} from '../../../utils/custom-utils'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'

/**
 * Image with text component
 *
 * @param {object} props
 * @param {string} - props.ITCLink - Image Link.
 * @param {string} - props.ITCText - Text Below Image.
 * @param {image} - props.image - Image.
 * @param {string} - props.heading - Text Overlay.
 * @param {string} - props.alt - The image alt text shown by the component.
 * @returns {React.ReactElement} - ImageWithText component.
 */
export const ImageTileWithOverlay = ({
    expectedDesktopImageHeight,
    expectedMobileImageHeight,
    tileUrl,
    imageLazyload,
    bgColor,
    textStyle,
    imageTitle = '',
    titleFontSize,
    imageSubTitle = '',
    subTitleFontSize,
    imageAltTag = '',
    subTitleColor,
    titleColor,
    borderRadius,
    target,
    custom
}) => {
    const {locale, site} = useMultiSite()
    const [imageLoaded, setImageLoaded] = useState(false)
    const hasCaption = imageTitle || imageSubTitle
    const isLazy = imageLazyload === 'Yes' ? 'lazy' : 'eager'
    const formattedTileUrl = getPDPath(tileUrl, site, locale)
    const styles = useMultiStyleConfig('ImagetileWithOverlay')
    const roundEdge = borderRadius === 'Yes'
    const targetLink = target === 'New-Window' ? '_blank' : '_self'
    const imageURL = custom?.image?.disBaseLink
    const mobileImageURL = custom?.mobileImage?.disBaseLink || imageURL
    const segment = useSegment()

    // Calculate skeleton height based on expected heights or fallback to default values
    const skeletonHeight = {
        base: expectedMobileImageHeight,
        md: expectedDesktopImageHeight
    }

    // Handle image onLoad event
    const handleImageLoad = () => {
        setImageLoaded(true)
    }

    const borderRadiusStyles = roundEdge
        ? {
              borderRadius: '8px'
          }
        : {}

    const imageTopBorderRadius = roundEdge
        ? {
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px'
          }
        : {}

    const captionBottomBorderRadius = roundEdge
        ? {
              borderBottomLeftRadius: '16px',
              borderBottomRightRadius: '16px'
          }
        : {}

    // Image content component
    const ImageContent = () => (
        <Box
            position="relative"
            width="100%"
            height={imageLoaded ? 'auto' : skeletonHeight}
            data-testid={'image-and-text-content'}
            {...imageTopBorderRadius}
            overflow="hidden"
        >
            {!imageLoaded && (
                <Skeleton
                    position="absolute"
                    top="0"
                    left="0"
                    width="100%"
                    height="100%"
                    startColor="gray.100"
                    endColor="gray.300"
                    zIndex="1"
                />
            )}
            <picture style={{opacity: imageLoaded ? 1 : 0, transition: 'opacity 0.3s ease'}}>
                {/* Desktop image */}
                <source srcSet={custom?.image?.disBaseLink} media="(min-width: 48em)" />
                {/* Mobile image */}
                <source srcSet={mobileImageURL} media="(max-width: 47.99em)" />
                {/* Fallback image */}
                <img
                    className="image-with-text-image"
                    data-testid="image-with-text-image"
                    src={mobileImageURL}
                    alt={imageAltTag}
                    loading={isLazy}
                    width={'100%'}
                    onLoad={handleImageLoad}
                />
            </picture>
        </Box>
    )

    // Caption Content component
    const CaptionContent = () => {
        if (!hasCaption) return null

        return (
            <Box
                as="figcaption"
                background={bgColor?.value}
                backgroundImage={`url(${getAssetUrl('static/img/bg_cta_area.jpg')})`}
                backgroundSize="cover"
                backgroundPosition="50%"
                textAlign={
                    textStyle === 'text-left'
                        ? 'left'
                        : textStyle === 'text-right'
                        ? 'right'
                        : 'center'
                }
                className="category-title"
                {...styles.textContainer}
                {...captionBottomBorderRadius}
            >
                {imageTitle && (
                    <Box
                        className={'heading text-clamp'}
                        sx={{
                            ...styles.heading,
                            mozBoxOrient: 'vertical',
                            WebkitBoxOrient: 'vertical',
                            boxOrient: 'vertical'
                        }}
                    >
                        <Box
                            fontSize={titleFontSize}
                            color={titleColor?.value}
                            dangerouslySetInnerHTML={{__html: imageTitle}}
                            suppressHydrationWarning
                        />
                    </Box>
                )}
                {imageSubTitle && (
                    <Box
                        className={'sub-heading text-clamp'}
                        sx={{
                            ...styles.subHeading,
                            mozBoxOrient: 'vertical',
                            WebkitBoxOrient: 'vertical',
                            boxOrient: 'vertical'
                        }}
                    >
                        <Box
                            fontSize={subTitleFontSize}
                            color={subTitleColor?.value}
                            dangerouslySetInnerHTML={{__html: imageSubTitle}}
                            suppressHydrationWarning
                        />
                    </Box>
                )}
            </Box>
        )
    }

    return (
        <Box className={'image-with-text'}>
            <Box
                as="figure"
                className={'image-with-text-figure'}
                position={'relative'}
                margin={0}
                w="100%"
                onClick={(event) => {
                    // segment
                    segment.sendPanelSelectedEvent(event, {})
                }}
                {...borderRadiusStyles}
            >
                {tileUrl ? (
                    <Link to={formattedTileUrl} display="block" target={targetLink}>
                        <ImageContent />
                        <CaptionContent />
                    </Link>
                ) : (
                    <>
                        <ImageContent />
                        <CaptionContent />
                    </>
                )}
            </Box>
        </Box>
    )
}

ImageTileWithOverlay.propTypes = {
    tileUrl: PropTypes.string,
    imageLazyload: PropTypes.string,
    bgColor: PropTypes.shape({
        value: PropTypes.string
    }),
    borderRadius: PropTypes.string,
    imageTitle: PropTypes.string,
    titleFontSize: PropTypes.string,
    titleColor: PropTypes.shape({
        value: PropTypes.string
    }),
    subTitleColor: PropTypes.shape({
        value: PropTypes.string
    }),
    imageSubTitle: PropTypes.string,
    subTitleFontSize: PropTypes.string,
    textStyle: PropTypes.string,
    imageAltTag: PropTypes.string,
    target: PropTypes.string,
    expectedDesktopImageHeight: PropTypes.string,
    expectedMobileImageHeight: PropTypes.string,
    custom: PropTypes.object
}

export default ImageTileWithOverlay
